import React, { useMemo } from 'react';

import { _2dMapQuoteTypes } from '@larki/shared/type/order.v2';
import classNames from 'classnames';
import WhiteContainer from '../../../components/containers/WhiteContainer';
import List from '../../../components/list/List';
import Loading from '../../../components/loading/Loading';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { orderV2Selectors } from '../../../redux/selectors/order.v2';
import text from '../../../text';
import { usePaymentContext } from '../../hooks/usePaymentContext';

const getMembershipName = (product: _2dMapQuoteTypes['product']) => {
  switch (product) {
    case 'essentials_monthly':
      return text('essentialsMembershipMonthly');
    case 'essentials_yearly':
      return text('essentialsMembershipYearly');
    default:
      return '';
  }
};

const MembershipInvoice = () => {
  const { jobId, isLoadingQuote, formatPrice, asMember } = usePaymentContext();
  const quote = useAppSelector((state) =>
    orderV2Selectors.getQuote(state, jobId, asMember)
  );
  const nonRfqCharges = useAppSelector((state) =>
    orderV2Selectors.getNonRfqCharges(state, jobId, asMember)
  );
  const membershipCharges = useMemo(
    () => (nonRfqCharges ?? [])?.filter((c) => c.group === 'membership'),
    [nonRfqCharges]
  );

  const show = isLoadingQuote || !!membershipCharges?.length;

  const rfqCharges = useAppSelector((state) =>
    orderV2Selectors.getRfqCharges(state, jobId, asMember)
  );

  const overriddenSet = useAppSelector((state) =>
    orderV2Selectors.getOverriddenSet(state, jobId, asMember)
  );

  const groupNumber = useMemo(() => {
    const nonRfqCount = quote?.nonRfq?.composition
      ? Object.values(quote.nonRfq.composition).length
      : 0;

    if (overriddenSet.size === 0) return nonRfqCount;

    const overriddenRfqGroupCount = rfqCharges
      ? new Set(
          rfqCharges
            .filter((c) => overriddenSet.has(c.product))
            .map((c) => c.group)
        ).size
      : 0;

    const hasOverriddenCustomOrders = (quote?.customOrders ?? []).some((o) =>
      overriddenSet.has(`custom_${o.id}`)
    );

    return (
      nonRfqCount +
      overriddenRfqGroupCount +
      (hasOverriddenCustomOrders ? 1 : 0)
    );
  }, [quote, rfqCharges, overriddenSet]);

  if (!show) return null;

  return (
    <WhiteContainer className={classNames('Invoice MembershipInvoice p-3')}>
      {isLoadingQuote ? (
        <div className='position-absolute overlay-loader-container'>
          <Loading />
        </div>
      ) : null}
      {membershipCharges?.length ? (
        <>
          <h6 className='green-1 font-weight--600 mb-0 mt-2 mx-2'>
            <span className='mr-1'>{groupNumber}.</span>
            <span>{text('membership')}</span>
          </h6>
          <div style={{ paddingLeft: '20px', paddingRight: '16px' }}>
            <List>
              {membershipCharges?.map((charge, i) => (
                // @ts-expect-error List.Item is not typed
                <List.Item
                  key={`${charge.product}-${i}`}
                  description={getMembershipName(charge.product)}
                  value={
                    charge.baseBeforeDiscounts === 'TBC'
                      ? text('toBeConfirmed')
                      : formatPrice(charge.baseBeforeDiscounts)
                  }
                />
              ))}
            </List>
          </div>
        </>
      ) : null}
    </WhiteContainer>
  );
};

export default MembershipInvoice;
